/*
 * engraving-cart-note.js
 *
 * Maintains the hidden cart note on the cart page. The note has two parts,
 * separated by ENGRAVING_SEPARATOR:
 *
 *   1. Customer note  — typed by the user in #customer-note
 *   2. Engraving data — auto-generated from cart items that have a
 *                       `Gravurtext` property (engraving service items)
 *
 * The final composed value is written to the hidden #cart-note input which
 * is submitted with the cart form.
 *
 * Data bridge: reads Liquid-generated values from the JSON island rendered
 * by sections/main-cart.liquid:
 *
 *   <script type="application/json" id="engraving-cart-note-data">
 *     { "cartUrl": "…", "cartNote": "…" }
 *   </script>
 *
 * Dual-position engraving compatibility
 * ---------------------------------------
 * When a customer engraves two positions, the cart contains two engraving
 * service items (one per position), each with a plain `Gravurtext` property
 * and a `Platzierung` property. The product item itself has
 * `Gravurtext (Klinge)` / `Gravurtext (Griff)` properties but NO plain
 * `Gravurtext` — so it is naturally skipped by `formatEngravingData`, and
 * the two service items are captured separately. No special-casing required.
 */

(function () {
  var dataEl = document.getElementById("engraving-cart-note-data");
  if (!dataEl) return;

  var noteData = JSON.parse(dataEl.textContent);
  var cartJsonUrl = noteData.cartUrl + ".js";

  // The separator keyword is configured via Theme Settings › Gravur › Bestellnotiz.
  // Changing it after orders exist will prevent existing notes from being parsed
  // correctly — see README.md for details.
  var separatorKeyword = noteData.noteSeparator || "[GRAVURDATEN]";
  var ENGRAVING_SEPARATOR = "\n\n" + separatorKeyword + "\n\n";

  var isRefreshingEngraving = false;
  var refreshTimeout = null;

  /* -------------------------------------------------------------------- *
   *  Helpers                                                             *
   * -------------------------------------------------------------------- */

  /**
   * Decodes HTML entities that Shopify's cart JSON API encodes in property
   * values (e.g. &quot; → ", &amp; → &, &#39; → ').
   */
  function decodeHtmlEntities(str) {
    var txt = document.createElement("textarea");
    txt.innerHTML = str;
    return txt.value;
  }

  function flatten(raw) {
    if (raw == null) return "";
    var str = Array.isArray(raw)
      ? raw.filter(Boolean).join(" und ")
      : String(raw);
    return decodeHtmlEntities(str.replace(/\s+/g, " ").trim());
  }

  /** Returns only the customer-written part, stripping any engraving block. */
  function getCustomerNoteOnly(value) {
    return (value || "").split(ENGRAVING_SEPARATOR)[0].trim();
  }

  /* -------------------------------------------------------------------- *
   *  Customer note initialisation                                        *
   * -------------------------------------------------------------------- */

  function initCustomerNote() {
    var customerNoteEl = document.getElementById("customer-note");
    if (!customerNoteEl) return;
    // Populate the visible textarea with just the human-written portion of
    // the stored cart note so the auto-generated engraving block stays hidden.
    customerNoteEl.value = getCustomerNoteOnly(noteData.cartNote);
  }

  /* -------------------------------------------------------------------- *
   *  Engraving data formatting                                          *
   * -------------------------------------------------------------------- */

  /**
   * Builds a multi-line engraving summary from the current cart snapshot.
   *
   * Items are grouped by their shared `_Gravur-ID` so each engraved product
   * appears exactly once, with its engraving positions listed below it:
   *
   *   Produkt: Messer XY | Menge: 1
   *     Gravurtext: "Hallo" | Schriftart: Druckschrift | Platzierung: Gravur auf Klinge
   *
   *   Produkt: Anderes Messer | Menge: 2
   *     Gravurtext: "Welt" | Schriftart: Schreibschrift | Platzierung: Gravur auf Klinge
   *     Gravurtext: "XYZ"  | Schriftart: Schreibschrift | Platzierung: Gravur auf Griff
   *
   * Because this function always receives a fresh cart snapshot (see
   * `refreshEngravingData`), items removed from the cart before checkout are
   * automatically absent — no additional filtering needed.
   *
   * Edge cases:
   *   - Product removed, service item still in cart: product title is taken
   *     from the service item's `Für` property.
   *   - Service item removed, product still in cart: the group has no detail
   *     lines and is skipped entirely (no active engraving service to report).
   */
  function formatEngravingData(cart) {
    if (!cart || !cart.items || cart.items.length === 0) return "";

    // ── 1. Group all engraving-related items by their _Gravur-ID ──────────
    var groups = {};

    cart.items.forEach(function (item) {
      var props = item.properties || {};
      var pairKey = flatten(props["_Gravur-ID"]);
      if (!pairKey) return; // Not part of an engraving set.

      if (!groups[pairKey]) {
        groups[pairKey] = { productItem: null, engravingItems: [] };
      }

      // Engraving service items carry a `Für` property (the product title).
      // The main product item does not.
      if (flatten(props["Für"])) {
        groups[pairKey].engravingItems.push(item);
      } else {
        groups[pairKey].productItem = item;
      }
    });

    // ── 2. Format each group ──────────────────────────────────────────────
    var blocks = [];

    Object.keys(groups).forEach(function (key) {
      var group = groups[key];

      // Skip groups where the service item(s) were removed from the cart.
      // The product may still be there, but without an active engraving
      // service there is nothing meaningful to report.
      if (group.engravingItems.length === 0) return;

      // Prefer the product item's live title; fall back to the `Für` string
      // stored on the service item (covers the case where the product itself
      // was removed but the service item remains).
      var productTitle = group.productItem
        ? flatten(group.productItem.product_title)
        : flatten((group.engravingItems[0].properties || {})["Für"]) ||
          "Unbekanntes Produkt";

      var quantity = group.productItem
        ? group.productItem.quantity
        : group.engravingItems[0].quantity;

      var lines = [
        "Produkt: " +
          productTitle +
          " | Menge: " +
          quantity +
          " | _Gravur-ID: " +
          key,
      ];

      // One detail line per engraving position (order mirrors the cart order).
      group.engravingItems.forEach(function (item) {
        var props = item.properties || {};
        lines.push(
          "  Gravurtext: " +
            flatten(props["Gravurtext"]) +
            " | Schriftart: " +
            flatten(props["Schriftart"]) +
            " | Platzierung: " +
            flatten(props["Platzierung"]) +
            " | _Gravur-ID: " +
            flatten(props["_Gravur-ID"]),
        );
      });

      blocks.push(lines.join("\n"));
    });

    return blocks.join("\n\n");
  }

  /* -------------------------------------------------------------------- *
   *  Note composition                                                    *
   * -------------------------------------------------------------------- */

  /**
   * Combines the customer note and the current engraving data into the
   * single hidden #cart-note input that the cart form submits.
   */
  function buildFinalNote() {
    var customerNoteEl = document.getElementById("customer-note");
    var engravingEl = document.getElementById("engraving-data");
    var cartNoteEl = document.getElementById("cart-note");

    if (!cartNoteEl) return;

    var customerNote = getCustomerNoteOnly(
      customerNoteEl ? customerNoteEl.value : "",
    );
    var engraving =
      engravingEl && engravingEl.value ? engravingEl.value.trim() : "";

    var finalNote = customerNote;
    if (engraving !== "") {
      finalNote += (finalNote ? ENGRAVING_SEPARATOR : "") + engraving;
    }

    cartNoteEl.value = finalNote;

    // Keep the visible textarea clean (customer portion only).
    if (customerNoteEl) {
      customerNoteEl.value = customerNote;
    }
  }

  /* -------------------------------------------------------------------- *
   *  Cart refresh                                                        *
   * -------------------------------------------------------------------- */

  function scheduleEngravingRefresh() {
    if (refreshTimeout) clearTimeout(refreshTimeout);
    var delay = noteData.debounceMs || 500;
    refreshTimeout = setTimeout(function () {
      refreshEngravingData();
    }, delay);
  }

  async function refreshEngravingData() {
    var engravingEl = document.getElementById("engraving-data");
    if (!engravingEl) return;

    if (isRefreshingEngraving) return;
    isRefreshingEngraving = true;

    try {
      var res = await fetch(cartJsonUrl, {
        headers: { Accept: "application/json" },
        cache: "no-store",
      });

      if (!res.ok) return;

      var cart = await res.json();

      engravingEl.value =
        cart.items && cart.items.length > 0 ? formatEngravingData(cart) : "";
    } catch (e) {
      console.error("[engraving] cart note refresh failed:", e);
      engravingEl.value = "";
    } finally {
      isRefreshingEngraving = false;
    }
  }

  /* -------------------------------------------------------------------- *
   *  Fetch interception                                                  *
   * -------------------------------------------------------------------- */

  // Intercept fetch so any cart mutation (add, change, remove) triggers a
  // fresh engraving-data render without needing a page reload.
  var originalFetch = window.fetch;

  window.fetch = async function () {
    var response = await originalFetch.apply(this, arguments);
    var url = arguments[0];

    if (typeof url === "string" && url.includes("/cart")) {
      scheduleEngravingRefresh();
    }

    return response;
  };

  /* -------------------------------------------------------------------- *
   *  Boot                                                                *
   * -------------------------------------------------------------------- */

  window.addEventListener("pageshow", function () {
    refreshEngravingData();
  });

  function init() {
    initCustomerNote();
    refreshEngravingData();

    var form = document.getElementById("cart");
    if (form) {
      // Compose the final note just before the cart form is submitted so
      // the engraving block is always current.
      form.addEventListener(
        "submit",
        function () {
          buildFinalNote();
        },
        true,
      );
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
